
import apiClient, { requestData } from "./apiClient";
import { cached, invalidate } from "./apiCache";
import { uploadToS3 } from "./s3Client";
import { getConfig } from "../../config";

export const authApi = {
  login: (payload) => apiClient.post("/api/auth/login", payload),
  googleLogin: (credential) => apiClient.post("/api/auth/google-login", { credential }),
  register: (payload) => apiClient.post("/api/auth/register", payload),
  forgotPassword: (email) => apiClient.post("/api/auth/forgot-password", { email }),
  getOrgInfo: () => cached("auth:orgInfo", () => requestData({ method: "GET", url: "/api/auth/org-info" })),
  saveOrgInfo: (payload) => {
    invalidate("auth:orgInfo");
    return apiClient.post("/api/auth/org-info", payload);
  },
  skipOrgInfo: () => {
    invalidate("auth:orgInfo");
    return apiClient.post("/api/auth/org-info/skip", {});
  },
};

export const uploadApi = {
  // Step 1 — Flask: validate filename, return presigned S3 PUT URL + job_id.
  // Pass `jobId` to reuse an existing job namespace (multi-file optimization
  // batches: every file after the first reuses the FIRST file's job_id so all
  // of them land under one job folder and confirm() can dispatch them as ONE task).
  init: (filename, contentType, jobId) =>
    apiClient.post("/api/upload/init", {
      filename,
      content_type: contentType,
      ...(jobId ? { job_id: jobId } : {}),
    }),

  // Step 2 — S3 direct: upload file bytes (see s3Client.js for why not apiClient)
  uploadToS3,

  // Step 3 — Flask: verify S3 arrival, dispatch ONE Celery task for the whole batch.
  // `files` is a list of {s3Key, fileName, fileType} — one entry for a single
  // file, several for a multi-file optimization batch. Always ONE task/ONE job
  // per upload action, never one task per file.
  confirm: (jobId, files) =>
    apiClient.post("/api/upload/confirm", {
      job_id: jobId,
      files: files.map(({ s3Key, fileName, fileType }) => ({
        s3_key:    s3Key,
        file_name: fileName,
        file_type: fileType,
      })),
    }),

  // Resume — returns active job {job_id, sse_token, file_name, started_at} or {}
  // Called on mount to restore the progress bar after a page refresh.
  getResumableJob: () => requestData({ method: "GET", url: "/api/upload/resume" }),

  // SSE — returns the stream URL with the short-lived OTP token in query string.
  // EventSource cannot send custom headers, so the token travels as ?token=
  // The OTP is generated server-side on /confirm and has a 2-hour TTL in Redis.
  // The hook creates the EventSource from this URL and manages its lifecycle.
  getStreamUrl: (jobId, sseToken) => {
    const { apiUrl } = getConfig();
    return `${apiUrl}/api/upload/stream/${jobId}?token=${encodeURIComponent(sseToken)}`;
  },
};

export const reportApi = {
  getDetails: () => cached("report:details", () => requestData({
    method: "POST",
    url: "/api/details/print_details",
    data: {},
  })),
  invalidateDetails: () => invalidate("report:details"),
};

export const notificationApi = {
  // Merged feed for the logged-in user: { items, unread_count }.
  list: () => requestData({ method: "GET", url: "/api/notifications" }),

  // Mark one / all notifications read (optimistic on the client; these persist it).
  markRead: (id) => apiClient.post(`/api/notifications/${id}/read`, {}),
  markAllRead: () => apiClient.post("/api/notifications/read-all", {}),

  // Publish a news broadcast (open behind JWT for now; server TODO to gate by admin).
  broadcast: (payload) => apiClient.post("/api/notifications/broadcast", payload),

  // Live SSE — same OTP pattern as uploads: POST (JWT) mints a short-lived token,
  // then EventSource connects with ?token= (it cannot send auth headers).
  getStreamToken: () =>
    requestData({ method: "POST", url: "/api/notifications/stream-token", data: {} }),
  getStreamUrl: (token) => {
    const { apiUrl } = getConfig();
    return `${apiUrl}/api/notifications/stream?token=${encodeURIComponent(token)}`;
  },
};

export const userPreferencesApi = {
  // Get user preferences
  getPreferences: () => requestData({ method: "GET", url: "/api/auth/preferences" }),

  // Save user preferences
  savePreferences: (prefs) => apiClient.post("/api/auth/preferences", prefs),
};

export const optimizationReportApi = {
  // Fetch the S3 metadata snapshot saved after the xlsx pipeline completes.
  getSummary: (jobId) =>
    requestData({ method: "GET", url: `/api/optimization-report/${jobId}/summary` }),

  // Fetch the full report data (discover + all device dashboard snapshots) saved
  // to S3 once at pipeline completion.  This is the primary data source for
  // the OverviewPage — one fetch, zero live calls to the analysis service.
  getReportData: (jobId) =>
    requestData({ method: "GET", url: `/api/optimization-report/${jobId}/report-data` }),

  // ── Finding acknowledgements — the report's only write path ────────────────
  // Keyed on the stable `issue_key` the analysis layer emits, so a state set today still
  // matches the same finding after next period's rebuild. Read separately from the report
  // itself because it changes on a different clock: the report is rebuilt per period, these
  // change whenever someone acts.
  getAcknowledgements: (jobId) =>
    requestData({ method: "GET", url: `/api/optimization-report/${jobId}/acknowledgements` }),

  setAcknowledgement: (jobId, { issue_key, state, note }) =>
    requestData({
      method: "POST",
      url: `/api/optimization-report/${jobId}/acknowledgements`,
      data: { issue_key, state, note },
    }),
};
